/**
 * sl-9qm.5.2: MaterializedViewDataSource — JSON snapshot-based data source
 *
 * Reads pre-materialized data from embedded JSON or REST API.
 * Used for AppBases where Y.js is not available.
 * Always read-only — no write operations.
 *
 * SOLID:
 * - S: Only reads materialized snapshot data
 * - L: Substitutable with YjsViewDataSource via IViewDataSource
 */
import type { ViewConfigRow } from '@streamlain/view-config';
import { type Observable, of } from 'rxjs';

import type { DataFilter, DataSort } from '../types/view-config.types';
import type {
  ColumnInfo,
  DatabaseViewData,
  DataSourceConfig,
  IViewDataSource,
  Row,
} from '../types/view-datasource.types';

/**
 * Materialized snapshot format — matches backend snapshot output (sl-9qm.2).
 */
export interface MaterializedSourceSnapshot {
  source: { key: string; blockId: string; docId: string };
  columns: ColumnInfo[];
  rows: Row[];
  title: string;
}

export interface MaterializedSnapshot {
  columns: ColumnInfo[];
  rows: Array<{ id: string; cells: Record<string, unknown> }>;
  viewConfig?: ViewConfigRow | null;
  sources?: Record<string, MaterializedSourceSnapshot>;
  appBase?: Record<string, unknown> | null;
  htmlBlocksRendered?: Record<string, Record<string, string>>;
  page?:
    | (Record<string, unknown> & {
        rowId?: string | null;
        sourceKey?: string | null;
      })
    | null;
  locale?: string;
  routeParams?: Record<string, string>;
  totalCount: number;
  generatedAt: string;
}

/**
 * Apply filters client-side on snapshot data.
 * Reuses same logic as YjsViewDataSource (DRY extracted).
 */
function matchFilter(cellValue: unknown, filter: DataFilter): boolean {
  const { op, value: filterValue } = filter;
  switch (op) {
    case 'isEmpty':
      return cellValue === null || cellValue === undefined || cellValue === '';
    case 'isNotEmpty':
      return cellValue !== null && cellValue !== undefined && cellValue !== '';
    case 'eq':
      return String(cellValue) === String(filterValue);
    case 'neq':
      return String(cellValue) !== String(filterValue);
    case 'contains':
      return String(cellValue ?? '')
        .toLowerCase()
        .includes(String(filterValue ?? '').toLowerCase());
    case 'gt':
      return Number(cellValue) > Number(filterValue);
    case 'lt':
      return Number(cellValue) < Number(filterValue);
    case 'gte':
      return Number(cellValue) >= Number(filterValue);
    case 'lte':
      return Number(cellValue) <= Number(filterValue);
    default:
      return true;
  }
}

function applyFilters(rows: Row[], filters: DataFilter[]): Row[] {
  if (filters.length === 0) return rows;
  return rows.filter(row =>
    filters.every(f => matchFilter(row.cells[f.columnId], f))
  );
}

function applySorts(rows: Row[], sorts: DataSort[]): Row[] {
  if (sorts.length === 0) return rows;
  return [...rows].sort((a, b) => {
    for (const sort of sorts) {
      const aStr = String(a.cells[sort.columnId] ?? '');
      const bStr = String(b.cells[sort.columnId] ?? '');
      const cmp = aStr.localeCompare(bStr, undefined, { numeric: true });
      if (cmp !== 0) return sort.direction === 'asc' ? cmp : -cmp;
    }
    return 0;
  });
}

function applyPagination(
  rows: Row[],
  pagination?: { page: number; pageSize: number }
): Row[] {
  if (!pagination) return rows;
  const start = (pagination.page - 1) * pagination.pageSize;
  return rows.slice(start, start + pagination.pageSize);
}

export class MaterializedViewDataSource implements IViewDataSource {
  readonly writable = false;

  constructor(readonly snapshot: MaterializedSnapshot) {}

  getViewData(config: DataSourceConfig): Observable<DatabaseViewData> {
    return of(this.readViewData(config));
  }

  readViewData(config: DataSourceConfig): DatabaseViewData {
    const source = this.resolveSource(config);
    const columns = config.visibleColumns?.length
      ? source.columns.filter(c => config.visibleColumns!.includes(c.id))
      : source.columns;

    let rows: Row[] = source.rows;
    rows = applyFilters(rows, config.filters ?? []);
    const totalCount = rows.length;
    rows = applySorts(rows, config.sorts ?? []);
    rows = applyPagination(rows, config.pagination);

    return {
      columns,
      rows,
      totalCount,
      writable: false,
    };
  }

  getRow(config: DataSourceConfig, rowId: string): Observable<Row | null> {
    const source = this.resolveSource(config);
    const row = source.rows.find(r => r.id === rowId) ?? null;
    return of(row);
  }

  private resolveSource(config: DataSourceConfig): {
    columns: ColumnInfo[];
    rows: Row[];
  } {
    const sources = this.snapshot.sources ?? {};
    const byKey = config.sourceKey ? sources[config.sourceKey] : null;
    const byBlockId = config.databaseBlockId
      ? Object.values(sources).find(
          source => source.source.blockId === config.databaseBlockId
        )
      : null;
    const source = byKey ?? byBlockId;
    const resolved = source
      ? { columns: source.columns, rows: source.rows }
      : { columns: this.snapshot.columns, rows: this.snapshot.rows };

    if (config.contextScope !== 'row') return resolved;
    const rowId = this.resolveRouteRowId();
    if (!rowId) return resolved;
    return {
      columns: resolved.columns,
      rows: resolved.rows.filter(row => row.id === rowId),
    };
  }

  private resolveRouteRowId(): string | null {
    const pageRowId = this.snapshot.page?.rowId;
    if (typeof pageRowId === 'string' && pageRowId.trim()) return pageRowId;
    const params = this.snapshot.routeParams ?? {};
    const paramRowId = params['rowId'] ?? params['id'];
    return paramRowId?.trim() ? paramRowId : null;
  }
}
